/**
 * Utility functions for creating tasks in various development tools
 */

/**
 * Generate a task creation URL for the specified development tool
 */
export function getTaskCreationUrl(
  devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear',
  instanceUrl: string,
  title: string,
  description?: string,
  dataPoints?: number
) {
  const formattedDescription = description || ''

  const encodedTitle = encodeURIComponent(title)
  const encodedDescription = encodeURIComponent(formattedDescription)

  switch (devTool) {
    case 'jira':
      return `https://${instanceUrl}/secure/CreateIssueDetails!init.jspa?summary=${encodedTitle}&description=${encodedDescription}&pid=10000&issuetype=10002`

    case 'monday':
      return `https://${instanceUrl}/boards/new-item?title=${encodedTitle}&description=${encodedDescription}`

    case 'asana':
      return `https://app.asana.com/0/${instanceUrl}/list?name=${encodedTitle}&notes=${encodedDescription}`

    case 'productboard':
      // For ProductBoard, we don't return a URL as we'll be using the API
      // instead of a direct URL. The instanceUrl here will be the API key.
      return ''

    case 'linear':
      // Linear supports creating issues via linear.new with prefilled fields
      return `https://linear.new?title=${encodedTitle}&description=${encodedDescription}`

    default:
      return ''
  }
}

/**
 * Build a unified task description from an Opportunity object
 * Order: Subtitle, PROBLEM, SOLUTION, KEY ELEMENTS, RABBIT HOLES
 */
export function buildOpportunityTaskDescription(opportunity: any): string {
  if (!opportunity) return ''

  const subtitle: string = opportunity.subtitle || opportunity.description || ''
  const problem: string = opportunity.PRD?.problem || ''
  const solution: string = opportunity.PRD?.solution || ''
  const keyElements: string[] = Array.isArray(opportunity.PRD?.key_elements) ? opportunity.PRD.key_elements : []
  const rabbitHoles: Array<{ title?: string; description?: string }> = Array.isArray(opportunity.PRD?.rabbit_holes)
    ? opportunity.PRD.rabbit_holes
    : []

  const lines: string[] = []

  if (subtitle) {
    lines.push(subtitle.trim())
  }

  if (problem) {
    lines.push('', 'PROBLEM', problem.trim())
  }

  if (solution) {
    lines.push('', 'SOLUTION', solution.trim())
  }

  if (keyElements.length > 0) {
    lines.push('', 'KEY ELEMENTS')
    for (const element of keyElements) {
      if (element && String(element).trim().length > 0) {
        lines.push(`• ${String(element).trim()}`)
      }
    }
  }

  if (rabbitHoles.length > 0) {
    lines.push('', 'RABBIT HOLES')
    for (const item of rabbitHoles) {
      const title = (item?.title || '').trim()
      const desc = (item?.description || '').trim()
      if (title || desc) {
        const combined = title && desc ? `${title}: ${desc}` : title || desc
        lines.push(`• ${combined}`)
      }
    }
  }

  return lines.join('\n')
}

/**
 * Create a ProductBoard feature using their API
 */
export async function createProductBoardFeature(apiKey: string, title: string, description?: string) {
  try {
    const formattedDescription = description || ''

    const response = await fetch('https://api.productboard.com/features', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Version': '1',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        name: title,
        description: formattedDescription,
        status: 'started', // default status for new features
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create ProductBoard feature: ${errorText}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error creating ProductBoard feature:', error)
    throw error
  }
}

/**
 * Get the instance URL for the specified development tool from localStorage
 */
export function getStoredInstanceUrl(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear'): string | null {
  const storageKey = `${devTool}InstanceUrl`
  return localStorage.getItem(storageKey)
}

/**
 * Save the instance URL for the specified development tool to localStorage
 */
export function saveInstanceUrl(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear', url: string): void {
  const storageKey = `${devTool}InstanceUrl`
  localStorage.setItem(storageKey, url.trim())
}

/**
 * Get the icon for the specified development tool
 */
export function getDevToolIcon(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear'): string {
  switch (devTool) {
    case 'asana':
      return '/lovable-uploads/589e044b-84ee-46bb-97ad-505af3f08495.png'
    case 'monday':
      return '/lovable-uploads/6928177a-c9c3-4df1-9d06-f64a4f5edac5.png'
    case 'productboard':
      return '/lovable-uploads/c8ad4133-99c4-45bd-bc13-4da90dc6e99f.png'
    case 'linear':
      return '/lovable-uploads/linear.svg'
    default:
      return '/lovable-uploads/7bb3a14d-1cbc-45ca-9e3c-ba2d642ff27c.png'
  }
}

/**
 * Get the placeholder text for the development tool's instance URL input
 */
export function getInstanceUrlPlaceholder(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear'): string {
  switch (devTool) {
    case 'jira':
      return 'yourcompany.atlassian.net'
    case 'monday':
      return 'yourcompany.monday.com'
    case 'asana':
      return '123456789'
    case 'productboard':
      return 'your-productboard-api-key'
    case 'linear':
      return 'Not required'
    default:
      return ''
  }
}

/**
 * Get the label for the development tool's instance URL input
 */
export function getInstanceUrlLabel(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear'): string {
  switch (devTool) {
    case 'jira':
      return 'Subdomain'
    case 'monday':
      return 'Instance ID'
    case 'asana':
      return 'Workspace ID'
    case 'productboard':
      return 'API Key'
    case 'linear':
      return 'Config'
    default:
      return ''
  }
}

/**
 * Get the help text for the development tool's instance URL input
 */
export function getInstanceUrlHelpText(devTool: 'jira' | 'monday' | 'asana' | 'productboard' | 'linear'): string {
  switch (devTool) {
    case 'jira':
      return 'Enter your Jira subdomain (e.g., "yourcompany.atlassian.net")'
    case 'monday':
      return 'Enter your Monday.com instance (e.g., "yourcompany.monday.com")'
    case 'asana':
      return 'Enter your Asana workspace ID'
    case 'productboard':
      return 'Enter your ProductBoard API key for direct integration'
    case 'linear':
      return 'No configuration needed. We will open linear.new to create the issue.'
    default:
      return ''
  }
}
